import * as React from 'react';
import { Route, Switch } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Redirect } from 'react-router';
import { Helmet } from 'react-helmet';

import { urls } from 'shared/entities/domain';
import {
  useAppNotificationsStore,
  useAuthFlowStore,
  useCabinetStore,
  useChannelsStore,
  useJobsStore,
  usePaymentStore,
  useUiStore,
  useUserStore
} from 'stores';
import SentryErrorBoundary from 'shared/components/SentryErrorBoundary';
import {
  useEventListener,
  useRouterStoreUpdates
} from 'shared/entities/common/hooks';
import LoaderSpinner from 'shared/newComponents/animations/LoaderSpinner';
import {
  LoaderSpinnerColor,
  LoaderSpinnerSize
} from 'shared/newEntities/components/LoaderSpinner';
import SupportPopup from 'shared/newContainers/SupportPopup';
import ChannelsModal from 'shared/newContainers/modals/ChannelsModal';
import { MAX_BANNER_ID } from 'stores/appNotificationsStore/AppNotificationsStore';
import AppLoadSpinner from 'shared/newComponents/AppLoadSpinner';
import TabTitle from 'shared/containers/TabTitle';
import { AppLoadSpinnerCenteredPosition } from 'shared/newEntities/components/AppLoadSpinner';

import NotificationBanner from '../NotificationBanner';

import ServiceModals from './ServiceModals';
import ProjectLoadContainer from './ProjectLoadContainer';

import './App.modules.scss';

const Auth = React.lazy(
  () => import(/* webpackChunkName: "page-auth" */ '../Auth')
);
const Subscription = React.lazy(
  () => import(/* webpackChunkName: "page-subscription" */ '../Subscription')
);

const App: React.FC = () => {
  const authFlowStore = useAuthFlowStore();
  const jobsStore = useJobsStore();
  const uiStore = useUiStore();
  const paymentStore = usePaymentStore();
  const cabinetStore = useCabinetStore();
  const userStore = useUserStore();
  const channelsStore = useChannelsStore();
  const appNotificationsStore = useAppNotificationsStore();
  useRouterStoreUpdates();

  React.useEffect(() => {
    authFlowStore.authorize();

    return (): void => {
      jobsStore.detach();
    };
  }, []);

  React.useEffect(() => {
    uiStore.checkMobileDevice();
    uiStore.changeSize({
      width: window.innerWidth,
      height: window.innerHeight
    });
  }, []);

  React.useEffect(() => {
    const getPaymentInfo = async () => {
      await paymentStore.getInfo();
      paymentStore.manageBannerAboutCreatedInvoice();
    };

    if (cabinetStore.initialized) {
      getPaymentInfo();
    }

    return () => {
      paymentStore.closeBannerAboutCreatedInvoice();
    };
  }, [cabinetStore.initialized]);

  React.useEffect(() => {
    if (cabinetStore.initialized) {
      userStore.loadUiSettings();
    }
  }, [cabinetStore.initialized]);

  React.useEffect(() => {
    if (!appNotificationsStore.banners.getEntity(MAX_BANNER_ID)) {
      appNotificationsStore.openMaxBanner();
    }

    return () => {
      appNotificationsStore.closeMaxBanner();
    };
  }, []);

  const resizeListener = React.useCallback(() => {
    uiStore.checkMobileDevice();
    uiStore.changeSize({
      width: window.innerWidth,
      height: window.innerHeight
    });
  }, []);

  useEventListener({ eventName: 'resize', handler: resizeListener });

  if (
    authFlowStore.authorizingStage.isLoading ||
    authFlowStore.authorizingStage.isNotStarted
  ) {
    return (
      <AppLoadSpinner
        centeredPosition={AppLoadSpinnerCenteredPosition.screen}
        size={LoaderSpinnerSize.lg}
      />
    );
  }

  return (
    <>
      <TabTitle />
      {uiStore.isMobile && (
        <Helmet>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
          />
        </Helmet>
      )}
      <SentryErrorBoundary>
        <div styleName="app">
          <div styleName="app__banner">
            <NotificationBanner />
          </div>
          <div styleName="app__content">
            <SentryErrorBoundary>
              <React.Suspense
                fallback={
                  <LoaderSpinner
                    color={LoaderSpinnerColor.blue}
                    centered
                    size={LoaderSpinnerSize.lg}
                  />
                }
              >
                <ChannelsModal
                  opened={channelsStore.channelsModalOpened.opened}
                  onClose={channelsStore.closeModal}
                />
                <Switch>
                  <Route component={Auth} path={urls.AUTH.mask} />
                  <Route
                    component={ProjectLoadContainer}
                    path={[
                      urls.PROJECT.mask,
                      urls.SETTINGS.mask,
                      urls.NEWS.mask
                    ]}
                  />
                  <Route
                    component={Subscription}
                    path={urls.SUBSCRIPTION.mask}
                  />
                  <Redirect to={urls.AUTH.root} />
                </Switch>
              </React.Suspense>
            </SentryErrorBoundary>
          </div>
        </div>
        <ServiceModals />
        <SupportPopup />
      </SentryErrorBoundary>
    </>
  );
};

export default observer(App);
